/**
 * Contract documents: the base agreement, BAA, addenda, amendments and
 * exhibits. Each one is read into the scope register when its text is on
 * file; a later document adds to the baseline, it never overwrites it.
 */
import React, { useRef, useState } from 'react';

const API = process.env.REACT_APP_API_URL;
const ROLE_LABEL = { contract: 'Contract', baa: 'BAA', amendment: 'Amendment', addendum: 'Addendum', exhibit: 'Exhibit' };
const ROLE_CLS = {
  contract: 'bg-slate-800 text-white', baa: 'bg-violet-100 text-violet-800', amendment: 'bg-sky-100 text-sky-800',
  addendum: 'bg-sky-100 text-sky-800', exhibit: 'bg-amber-100 text-amber-800',
};
// A DATE column arrives as 'YYYY-MM-DD…'; read it as a calendar day, never as UTC midnight localized (the day-early bug).
const fmt = (d) => {
  if (!d) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(d));
  const dt = m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(d);
  return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

export default function DocumentsTab({ contractId, documents = [], runs = [], reload }) {
  const [role, setRole] = useState('contract');
  const [effective, setEffective] = useState('');
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const [viewId, setViewId] = useState(null);
  const fileRef = useRef(null);
  const runByDoc = Object.fromEntries(runs.map((r) => [r.document_id, r]));

  const upload = async (file) => {
    if (!file) return;
    setUploading(true); setErr('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('doc_role', role);
      if (effective) fd.append('effective_date', effective);
      const res = await fetch(`${API}/api/fdr/contracts/${contractId}/document`, { method: 'POST', credentials: 'include', body: fd });
      const d = await res.json();
      if (!d.success) throw new Error(d.message || 'Upload failed');
      await reload();
    } catch (e) { setErr(e.message); }
    setUploading(false);
  };

  const post = async (url, label) => {
    setBusy(label); setErr('');
    try {
      const res = await fetch(url, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: '{}' });
      const d = await res.json();
      if (!d.success) throw new Error(d.message || 'Failed');
      await reload();
    } catch (e) { setErr(e.message); }
    setBusy('');
  };

  const readAll = () => post(`${API}/api/fdr/contracts/${contractId}/scope/run`, 'all');
  const readOne = (doc) => {
    setBusy(doc.id); setErr('');
    fetch(`${API}/api/fdr/contracts/${contractId}/scope/run`, {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ document_id: doc.id }),
    }).then((r) => r.json()).then((d) => { if (!d.success) throw new Error(d.message || 'Failed'); return reload(); })
      .catch((e) => setErr(e.message)).finally(() => setBusy(''));
  };
  const reread = (doc) => post(`${API}/api/fdr/contracts/${contractId}/documents/${doc.id}/reextract`, `re-${doc.id}`);

  const readStatus = (doc) => {
    const r = runByDoc[doc.id];
    if (r && ['queued', 'reading'].includes(r.status)) return <span className="text-sky-700">Reading… {r.progress_pct || 0}%{r.progress_msg ? ` · ${r.progress_msg}` : ''}</span>;
    if (r && r.status === 'failed') return <span className="text-red-600" title={r.error_message || ''}>Read failed{r.error_message ? `: ${r.error_message}` : ''}</span>;
    if (doc.scope_read_status === 'completed' || (r && r.status === 'completed')) {
      return <span className="text-emerald-700">Read into register{doc.scope_read_at ? ` · ${fmt(doc.scope_read_at)}` : ''}{r?.items_found != null ? ` · ${r.items_found} statements` : ''}{r?.items_superseded ? `, ${r.items_superseded} superseded` : ''}</span>;
    }
    return <span className="text-slate-400">Not read yet</span>;
  };

  const extraction = (doc) => {
    if (doc.extraction_status === 'completed') return <span className="text-slate-500">{doc.page_count ? `${doc.page_count} pages` : 'text on file'}{doc.has_pages ? '' : doc.page_count ? ' · no page anchors' : ''}</span>;
    if (doc.extraction_status === 'failed') return <span className="text-red-600" title={doc.extraction_error || ''}>Text could not be read{doc.extraction_error ? `: ${doc.extraction_error}` : ''}</span>;
    if (doc.extraction_status === 'blocked_phi') return <span className="text-red-600">Quarantined (PHI)</span>;
    return <span className="text-sky-700">Reading text…</span>;
  };

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm px-4 py-3 flex flex-wrap items-end gap-3">
        <input ref={fileRef} type="file" accept=".pdf,.docx,.doc,.txt,.md" className="hidden"
          onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = ''; }} />
        <label className="text-xs text-slate-600">
          <span className="block text-[10.5px] uppercase tracking-wide text-slate-400 mb-1">What is this file</span>
          <select value={role} onChange={(e) => setRole(e.target.value)} className="text-xs border border-slate-300 rounded-lg px-2 py-1.5 text-slate-700">
            <option value="contract">Contract (base agreement)</option>
            <option value="baa">Business Associate Agreement</option>
            <option value="addendum">Addendum</option>
            <option value="amendment">Amendment</option>
            <option value="exhibit">Exhibit or attachment</option>
          </select>
        </label>
        <label className="text-xs text-slate-600">
          <span className="block text-[10.5px] uppercase tracking-wide text-slate-400 mb-1">Effective date (optional)</span>
          <input type="date" value={effective} onChange={(e) => setEffective(e.target.value)} className="text-xs border border-slate-300 rounded-lg px-2 py-1.5 text-slate-700" />
        </label>
        <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
          className="px-4 py-2 rounded-xl bg-gradient-to-r from-orange-500 to-orange-600 text-white text-xs font-semibold shadow disabled:opacity-50">
          {uploading ? 'Uploading…' : `↑ Upload ${ROLE_LABEL[role]}`}
        </button>
        {documents.length > 0 && (
          <button type="button" onClick={readAll} disabled={busy === 'all'}
            className="px-3 py-2 rounded-xl border border-slate-300 text-slate-700 text-xs font-semibold hover:bg-slate-50 disabled:opacity-50"
            title="Read every document into the scope register, base agreement first">
            {busy === 'all' ? 'Starting…' : 'Read all into register'}
          </button>
        )}
        <span className="text-[11px] text-slate-400 ml-auto">PDF or Word, up to 50 MB. Scanned copies are read with OCR.</span>
      </div>

      {err && <div className="text-xs text-red-600 bg-red-50 rounded-lg px-3 py-2">{err}</div>}

      {!documents.length && (
        <div className="text-sm text-slate-500 bg-slate-50 border border-dashed border-slate-200 rounded-xl px-4 py-6 text-center">
          Upload the executed agreement first, then the BAA and any addenda or exhibits. Each one is read into the register on top of what is already there.
        </div>
      )}

      {documents.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm divide-y divide-slate-100">
          {documents.map((d) => {
            const isPdf = /pdf/i.test(d.mime_type || d.file_name || '');
            const url = `${API}/api/fdr/contracts/${contractId}/documents/${d.id}/file`;
            const reading = runByDoc[d.id] && ['queued', 'reading'].includes(runByDoc[d.id].status);
            return (
              <div key={d.id} className="px-4 py-3">
                <div className="flex flex-wrap items-center gap-3">
                  <span className={`px-2 py-0.5 rounded-md text-[10.5px] font-bold uppercase tracking-wide ${ROLE_CLS[d.doc_role] || ROLE_CLS.contract}`}>{ROLE_LABEL[d.doc_role] || 'Document'}</span>
                  <span className="text-[13px] font-semibold text-slate-800 truncate max-w-[420px]" title={d.file_name}>{d.file_name}</span>
                  {d.effective_date && <span className="text-[11.5px] text-slate-500">effective {fmt(d.effective_date)}</span>}
                  <span className="text-[11.5px] text-slate-400">added {fmt(d.created_at)}</span>
                  <div className="ml-auto flex items-center gap-2">
                    <button type="button" onClick={() => setViewId(viewId === d.id ? null : d.id)} className="text-[11.5px] font-semibold text-slate-600 hover:text-slate-900">{viewId === d.id ? 'Hide' : 'View'}</button>
                    <a href={isPdf ? `${url}?inline=1` : url} target="_blank" rel="noreferrer" className="text-[11.5px] font-semibold text-slate-600 hover:text-slate-900">Open ↗</a>
                    <button type="button" onClick={() => readOne(d)} disabled={busy === d.id || reading || d.extraction_status !== 'completed'}
                      className="text-[11.5px] font-semibold text-orange-700 hover:text-orange-900 disabled:opacity-40"
                      title="Read this document into the scope register (adds to what is on file)">
                      {reading ? 'Reading…' : d.scope_read_status === 'completed' ? 'Read again' : 'Read into register'}
                    </button>
                    <button type="button" onClick={() => reread(d)} disabled={busy === `re-${d.id}`}
                      className="text-[11.5px] text-slate-400 hover:text-slate-700 disabled:opacity-40" title="Extract the text again (for example after OCR improved)">
                      Re-read text
                    </button>
                  </div>
                </div>
                <div className="mt-1 flex flex-wrap gap-x-4 text-[11.5px]">
                  {extraction(d)}
                  {readStatus(d)}
                </div>
                {viewId === d.id && (
                  <div className="mt-3 border border-slate-200 rounded-xl overflow-hidden bg-slate-50">
                    {isPdf ? (
                      <iframe title={d.file_name} src={`${url}?inline=1`} className="w-full bg-white" style={{ height: '72vh', border: 0 }} />
                    ) : (
                      <div className="px-4 py-6 text-center text-xs text-slate-500">This file type does not preview here. <a className="font-semibold text-orange-700" href={url}>Download {d.file_name}</a>.</div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
